// ============================================================================
// USE OPERATIONS CLOUD STORAGE - Hook for cloud storage integration
// Provides operations drive access for DrawingManagerV3
// Date: January 2026
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { authService } from '@/lib/authService';

export interface CloudFolder {
  id: string;
  drive_id: string;
  parent_id: string | null;
  name: string;
  path: string;
  depth: number;
  icon?: string;
  is_favorite: boolean;
  is_pinned: boolean;
  file_count: number;
  total_size: number;
  children?: CloudFolder[];
  created_at: string;
  updated_at: string;
}

export interface CloudDrive {
  id: string;
  name: string;
  description?: string;
  drive_type: string;
  quota_bytes: number;
  used_bytes: number;
  is_active: boolean;
  created_at: string;
}

export interface CloudStorageStats {
  total_files: number;
  total_used: number;
  quota_bytes: number;
  usage_percent: number;
}

interface UseOperationsCloudStorageResult {
  drive: CloudDrive | null;
  folders: CloudFolder[];
  stats: CloudStorageStats | null;
  loading: boolean;
  error: string | null;
  isAdmin: boolean;
  currentUserId: number | null;
  refreshDrive: () => Promise<void>;
  createFolder: (name: string, parentId?: string | null, icon?: string) => Promise<CloudFolder | null>;
  getFolderTree: () => CloudFolder[];
}

/**
 * Hook for accessing the Operations Drive (system_operations)
 * Used by DrawingManagerV3 for cloud storage integration
 */
export function useOperationsCloudStorage(): UseOperationsCloudStorageResult {
  const [drive, setDrive] = useState<CloudDrive | null>(null);
  const [folders, setFolders] = useState<CloudFolder[]>([]);
  const [stats, setStats] = useState<CloudStorageStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);

  const fetchDrive = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = authService.getTokens()?.access_token;
      const response = await fetch('/api/v1/cloud-storage/operations-drive', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({ error: 'Failed to load' }));
        throw new Error(data.error || 'Failed to load operations drive');
      }

      const data = await response.json();
      
      if (data.success) {
        setDrive(data.drive);
        setFolders(data.folders || []);
        setStats(data.stats);
        setIsAdmin(data.isAdmin);
        setCurrentUserId(data.userId);
      } else {
        throw new Error(data.error || 'Failed to load operations drive');
      }
    } catch (err: any) {
      console.error('Operations drive error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load on mount
  useEffect(() => {
    fetchDrive();
  }, [fetchDrive]);

  // Create a folder in the operations drive
  const createFolder = useCallback(async (
    name: string,
    parentId?: string | null,
    icon?: string
  ): Promise<CloudFolder | null> => {
    try {
      const token = authService.getTokens()?.access_token;
      const response = await fetch('/api/v1/cloud-storage/operations-drive', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name, parent_id: parentId, icon })
      });

      const data = await response.json();
      
      if (data.success && data.folder) {
        // Refresh the folder list
        await fetchDrive();
        return data.folder;
      }
      
      throw new Error(data.error || 'Failed to create folder');
    } catch (err: any) {
      console.error('Create folder error:', err);
      setError(err.message);
      return null;
    }
  }, [fetchDrive]);

  // Build folder tree from flat list
  const getFolderTree = useCallback((): CloudFolder[] => {
    const folderMap = new Map<string, CloudFolder>();
    const roots: CloudFolder[] = [];

    // First pass: create map
    folders.forEach(folder => {
      folderMap.set(folder.id, { ...folder, children: [] });
    });

    // Second pass: build tree
    folders.forEach(folder => {
      const node = folderMap.get(folder.id)!;
      if (folder.parent_id && folderMap.has(folder.parent_id)) {
        const parent = folderMap.get(folder.parent_id)!;
        parent.children = parent.children || [];
        parent.children.push(node);
      } else if (folder.depth === 1) {
        // Root level folder
        roots.push(node);
      }
    });

    // Sort by name
    const sortFolders = (list: CloudFolder[]) => {
      list.sort((a, b) => a.name.localeCompare(b.name));
      list.forEach(f => {
        if (f.children?.length) {
          sortFolders(f.children);
        }
      });
    };

    sortFolders(roots);
    return roots;
  }, [folders]);

  return {
    drive,
    folders,
    stats,
    loading,
    error,
    isAdmin,
    currentUserId,
    refreshDrive: fetchDrive,
    createFolder,
    getFolderTree
  };
}

export default useOperationsCloudStorage;
